import { IconArrowLeft, IconArrowRight, IconBuildingBank } from "@tabler/icons-react";
import { useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useMisOrganizaciones } from "@/features/organizacion-manual/hooks/use-mis-organizaciones";
import { useSeleccionarOrganizacionManual } from "@/features/organizacion-manual/hooks/use-seleccionar-organizacion-manual";
import { useEsDueno } from "@/features/solicitudes/hooks/use-es-dueno";
import { useSolicitud } from "@/features/solicitudes/hooks/use-solicitud";
import { Button, Card, Group, PageHeader, ReadOnlyBanner, Stack, Text, ThemeIcon } from "@/components/ui";
import { LayoutConStepper, PASO_PAGE_SHELL } from "@/components/ui/solicitud-stepper";
import { Route } from "@/routes/_authed-tw/solicitudes/$id/organizacion-manual";

/**
 * P44 · FB60 Contrapago · primer paso de captura: no hay CFDI del que derivar
 * la organización (RN1), así que el solicitante la elige manualmente entre
 * las que tiene permitidas.
 */
export function OrganizacionManualPage() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const { data: sol } = useSolicitud(id);
  const { data: organizaciones } = useMisOrganizaciones();

  const esDueno = useEsDueno(sol);
  const puedeEditar = esDueno && (sol.estado === "SubtipoSeleccionado" || sol.estado === "Validado");

  const [seleccionada, setSeleccionada] = useState<string | null>(sol.organizacion?.id ?? null);
  const asociar = useSeleccionarOrganizacionManual(id);

  return (
    <div className="w-full px-4 py-6 md:px-6" style={PASO_PAGE_SHELL}>
      <PageHeader
        title="Organización"
        description="FB60 Contrapago · elige la organización del grupo contra la que se pagará."
        breadcrumbs={[
          { label: "Inicio", href: "/dashboard" },
          { label: "Solicitudes", href: "/solicitudes" },
          { label: id.slice(0, 8), href: `/solicitudes/${id}` },
          { label: "Organización" },
        ]}
      />
      <LayoutConStepper estado={sol.estado} subtipo={sol.subtipo} tipo={sol.tipo} prorrateoAutomatico={sol.prorrateoAutomatico} paso="organizacion" solicitudId={id} contabilizada={sol.enviadaAContabilizacion} fillHeight>
        <Stack gap="md">
          {!puedeEditar && <ReadOnlyBanner motivo={esDueno ? "post-envio" : "no-dueno"} mensaje="La organización asignada no puede modificarse." />}
          <Card padding="lg">
            <Stack gap="md">
              <div>
                <Text fw={600} size="sm">
                  Organizaciones disponibles
                </Text>
                <Text size="sm" c="dimmed" className="mt-0.5">
                  Como Contrapago no tiene un CFDI del que derivar el RFC receptor, selecciona la organización que corresponde.
                </Text>
              </div>
              {organizaciones.length === 0 ? (
                <Text size="sm" c="dimmed">
                  No tienes ninguna organización asignada · contacta a un administrador.
                </Text>
              ) : (
                <Stack gap="sm">
                  {organizaciones.map((org) => {
                    const activa = seleccionada === org.id;
                    return (
                      <button
                        key={org.id}
                        type="button"
                        disabled={!puedeEditar}
                        onClick={() => puedeEditar && setSeleccionada(org.id)}
                        className="rounded-[10px] border p-3.5 text-left transition-colors duration-120 disabled:cursor-not-allowed disabled:opacity-60"
                        style={{
                          borderColor: activa ? "var(--color-multiva-500)" : "var(--color-border)",
                          backgroundColor: activa ? "color-mix(in srgb, var(--color-multiva-600) 8%, transparent)" : "var(--color-background)",
                        }}
                      >
                        <Group gap="md" wrap="nowrap">
                          <span
                            aria-hidden="true"
                            className="flex size-4 shrink-0 items-center justify-center rounded-full border"
                            style={{ borderColor: activa ? "var(--color-multiva-600)" : "var(--color-input)" }}
                          >
                            {activa && <span className="size-2 rounded-full bg-multiva-600" />}
                          </span>
                          <ThemeIcon variant={activa ? "filled" : "light"} color="multiva" size="lg">
                            <IconBuildingBank size={20} stroke={1.6} />
                          </ThemeIcon>
                          <Stack gap={0} className="min-w-0 flex-1">
                            <Text fw={600} size="sm">
                              {org.nombre}
                            </Text>
                            <Text size="xs" c="dimmed">
                              RFC {org.rfc} · Sociedad {org.numeroSociedad}
                            </Text>
                          </Stack>
                        </Group>
                      </button>
                    );
                  })}
                </Stack>
              )}
            </Stack>
          </Card>
          <Group justify="space-between">
            <Button variant="default" leftSection={<IconArrowLeft size={14} />} onClick={() => navigate({ to: "/solicitudes/$id", params: { id } })}>
              Volver a la solicitud
            </Button>
            {puedeEditar && (
              <Button
                rightSection={<IconArrowRight size={14} />}
                disabled={!seleccionada}
                loading={asociar.isPending}
                onClick={() =>
                  seleccionada &&
                  asociar.mutate(seleccionada, {
                    onSuccess: () => navigate({ to: "/solicitudes/$id/datos-generales", params: { id } }),
                  })
                }
              >
                {asociar.isPending ? "Asignando…" : "Continuar a datos generales"}
              </Button>
            )}
          </Group>
        </Stack>
      </LayoutConStepper>
    </div>
  );
}
